import { useCallback } from 'react';
import { useSubjectLanguageVariants } from '@features/user';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import { useBookingGates } from '../store/hooks';
import type { Checkpoint, GateKind, PatientApplicability } from '../types';

import {
  BookingGatesFunnelView,
  type FunnelGate,
} from './BookingGatesFunnelView';

/** Gate kinds the funnel lets you place. `fit` is a coming-soon teaser and
 * `eligibility` drives the coverage toggle, so neither is a placeable row. */
const PLACEABLE_KINDS: GateKind[] = ['card', 'paperwork'];

/** Checkpoint stored for the enrichment eligibility gate. Enrichment never
 * blocks, so the checkpoint is inert — we pick the eligibility default. */
const ELIGIBILITY_CHECKPOINT: Checkpoint = 'before_calendar';

/** Container: maps the funnel model onto the gate API. Placing a gate creates
 * it at that checkpoint, moving patches the checkpoint, removing deletes it,
 * and the coverage toggle creates/deletes an enrichment eligibility gate. */
export function BookingGatesFunnelSection() {
  const { gatesByKind, isLoading, error, addGate, updateGateById, removeGate } =
    useBookingGates();
  const { plural } = useSubjectLanguageVariants();
  const { enqueueSnackbar } = useSnackbar();
  const { schedulingSettings, error: errorTracking } = useEventTracking();

  const run = useCallback(
    (fn: () => Promise<unknown>, failureMessage: string, errorKey: string) => {
      void (async () => {
        try {
          await fn();
        } catch {
          enqueueSnackbar(failureMessage, { variant: 'error' });
          errorTracking.track(failureMessage, errorKey);
        }
      })();
    },
    [enqueueSnackbar, errorTracking],
  );

  const gates: FunnelGate[] = PLACEABLE_KINDS.flatMap(kind => {
    const gate = gatesByKind[kind];
    return gate
      ? [{ kind, checkpoint: gate.checkpoint, appliesTo: gate.applies_to_patient }]
      : [];
  });
  const showCoverage = Boolean(gatesByKind.eligibility);

  const handleAddGate = useCallback(
    (kind: GateKind, checkpoint: Checkpoint) =>
      run(
        async () => {
          await addGate(kind, checkpoint);
          schedulingSettings.gateToggled({ gate: kind, enabled: true });
        },
        'Couldn’t add that check',
        'scheduling_gate_add',
      ),
    [run, addGate, schedulingSettings],
  );

  const handleMoveGate = useCallback(
    (kind: GateKind, checkpoint: Checkpoint) => {
      const gate = gatesByKind[kind];
      if (!gate) return;
      run(
        async () => {
          await updateGateById(gate.policy_id, { checkpoint });
          schedulingSettings.gateChanged({ gate: kind, field: 'checkpoint' });
        },
        'Couldn’t move that check',
        'scheduling_gate_move',
      );
    },
    [run, gatesByKind, updateGateById, schedulingSettings],
  );

  const handleRemoveGate = useCallback(
    (kind: GateKind) => {
      const gate = gatesByKind[kind];
      if (!gate) return;
      run(
        async () => {
          await removeGate(gate.policy_id);
          schedulingSettings.gateToggled({ gate: kind, enabled: false });
        },
        'Couldn’t remove that check',
        'scheduling_gate_remove',
      );
    },
    [run, gatesByKind, removeGate, schedulingSettings],
  );

  const handleChangeAppliesTo = useCallback(
    (kind: GateKind, value: PatientApplicability) => {
      const gate = gatesByKind[kind];
      if (!gate) return;
      run(
        async () => {
          await updateGateById(gate.policy_id, { applies_to_patient: value });
          schedulingSettings.gateChanged({
            gate: kind,
            field: 'applies_to_patient',
          });
        },
        'Couldn’t update who this applies to',
        'scheduling_gate_applies_to',
      );
    },
    [run, gatesByKind, updateGateById, schedulingSettings],
  );

  const handleToggleCoverage = useCallback(
    (enabled: boolean) => {
      const existing = gatesByKind.eligibility;
      run(
        async () => {
          if (enabled && !existing) {
            await addGate('eligibility', ELIGIBILITY_CHECKPOINT, {
              mode: 'enrichment',
            });
          } else if (!enabled && existing) {
            await removeGate(existing.policy_id);
          }
          schedulingSettings.gateToggled({ gate: 'eligibility', enabled });
        },
        'Couldn’t update coverage display',
        'scheduling_gate_coverage',
      );
    },
    [run, gatesByKind, addGate, removeGate, schedulingSettings],
  );

  return (
    <BookingGatesFunnelView
      gates={gates}
      showCoverage={showCoverage}
      subjectPlural={plural}
      isLoading={isLoading}
      hasError={Boolean(error)}
      onAddGate={handleAddGate}
      onMoveGate={handleMoveGate}
      onRemoveGate={handleRemoveGate}
      onChangeAppliesTo={handleChangeAppliesTo}
      onToggleCoverage={handleToggleCoverage}
    />
  );
}
